import { unref } from 'vue';

import { isVariableDefinedNotNull, urlParam } from '@slideslive/fuse-kit/utils';

import useStateStore from '@/stores/state';
import handleRequestError from '@/utils/handle_request_error';
import handleRequestResponse from '@/utils/handle_request_response';

const METHODS = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  PATCH: 'patch',
  DELETE: 'delete',
};

function normalizeBaseUrl(baseUrl) {
  const v = String(baseUrl || '').trim();
  if (!v) return '';
  return v.endsWith('/') ? v.slice(0, -1) : v;
}

function buildApiBaseUrl() {
  const nateRunnerHost = urlParam('nate_runner_host');
  if (isVariableDefinedNotNull(nateRunnerHost) && nateRunnerHost) {
    if (
      nateRunnerHost.startsWith('http://') ||
      nateRunnerHost.startsWith('https://') ||
      nateRunnerHost.startsWith('//')
    )
      return normalizeBaseUrl(nateRunnerHost);

    return normalizeBaseUrl(`//${nateRunnerHost}`);
  }

  return `//${window.location.hostname}:5004`;
}

function toQuery(params) {
  if (!params || typeof params !== 'object') return '';

  const qs = new URLSearchParams();

  Object.entries(params).forEach(([k, v]) => {
    if (v == null) return;
    qs.set(k, String(v));
  });

  const str = qs.toString();
  return str ? `?${str}` : '';
}

function buildMeta({ method, path, params, payload }) {
  return {
    method: String(method || '').toUpperCase(),
    path: String(path || ''),
    params: params ?? null,
    payload: payload ?? null,
  };
}

export default class RealDataFetcher {
  constructor() {
    this.apiBaseUrl = buildApiBaseUrl();

    console.info(`Using RealDataFetcher with apiBaseUrl set to '${this.apiBaseUrl || '(same-origin)'}'.`);
  }

  async #makeRequest(
    method,
    path,
    data = null,
    { abortController = null, contentType = 'application/json', params = null } = {},
  ) {
    const meta = buildMeta({ method, path, params, payload: data });

    try {
      const base = normalizeBaseUrl(this.apiBaseUrl);
      const p = String(path || '').trim();
      const url = `${base}${p}${method === METHODS.GET ? toQuery(params) : ''}`;

      const headers = {};
      if (contentType) headers['Content-Type'] = contentType;

      const stateStore = useStateStore();
      const sid = unref(stateStore.sessionId);

      if (sid != null && String(sid).trim()) {
        headers['X-SlidesLive-NATE-RUNNER-Session-Id'] = String(sid).trim();
      }

      const fetchOptions = {
        method: String(method || '').toUpperCase(),
        headers,
        signal: abortController?.signal,
      };

      if (data != null && method !== METHODS.GET) {
        fetchOptions.body = contentType === 'application/json' ? JSON.stringify(data) : data;
      }

      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        const txt = await response.text().catch(() => '');
        const err = new Error(`HTTP error! status: ${response.status}${txt ? `\n${txt}` : ''}`);
        err.status = response.status;
        err.responseText = txt;
        throw err;
      }

      const res = contentType === 'text/markdown' ? await response.text() : await response.json();

      try {
        handleRequestResponse(res, meta);
      } catch (e) {
        console.warn('handleRequestResponse failed', e);
      }

      return res;
    } catch (err) {
      try {
        handleRequestError(err, meta);
      } catch (e) {
        console.warn('handleRequestError failed', e);
      }

      throw err;
    }
  }

  buildUrl(path) {
    return `${this.apiBaseUrl}${String(path || '')}`;
  }

  async get(path, params = null, options = {}) {
    return this.#makeRequest(METHODS.GET, path, null, { ...options, params });
  }

  async post(path, data, options = {}) {
    return this.#makeRequest(METHODS.POST, path, data, options);
  }

  async patch(path, data, options = {}) {
    return this.#makeRequest(METHODS.PATCH, path, data, options);
  }

  async put(path, data, options = {}) {
    return this.#makeRequest(METHODS.PUT, path, data, options);
  }

  async delete(path, data, options = {}) {
    return this.#makeRequest(METHODS.DELETE, path, data, options);
  }
}
